import React from 'react';
import { ArrowRightIcon } from 'lucide-react';
import { BRAND, BOOK_DEMO_URL, GET_STARTED_URL } from '../data/dgpt';
import { VC_STAGES } from '../data/platform';
import { VC_FAQ } from '../data/ventureContent';
import { usePageMeta, canonicalUrl } from '../hooks/usePageMeta';
import { PageHero } from '../components/platform/PageHero';
import { CtaButton } from '../components/ui/CtaButton';
import { SectionHeading } from '../components/ui/SectionHeading';
import { Reveal } from '../components/ui/Reveal';
import { FaqList } from '../components/ui/FaqList';
import { ProcessFlow } from '../components/platform/ProcessFlow';
import { ProcessCta } from '../components/platform/ProcessCta';
import { VideoFacade } from '../components/dgpt/VideoFacade';
import { VcCapabilities } from '../components/venture/VcCapabilities';
import { InstitutionalMemory } from '../components/home/InstitutionalMemory';
import { SampleOutputSection } from '../components/home/SampleOutputSection';
import { EvidenceSection } from '../components/home/EvidenceSection';
import { ToolsGallery } from '../components/home/ToolsGallery';
import { MobileStickyCta } from '../components/dgpt/MobileStickyCta';
const DESCRIPTION = 'DiligenceGPT™ for venture capital: AI agents for deal intake, thesis fit, Deal Radar, structured diligence, IC memos and portfolio monitoring. Diligence at the Speed of Dealflow.';
export function VentureCapital() {
  usePageMeta({
    title: 'DiligenceGPT™ for Venture Capital | AI That Runs Your Dealflow',
    description: DESCRIPTION,
    path: '/venture-capital',
    keywords: ['AI due diligence for venture capital', 'investment thesis screening', 'AI investment memo generator', 'VC deal flow software', 'portfolio monitoring'],
    jsonLd: [{
      '@type': 'BreadcrumbList',
      itemListElement: [{
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: canonicalUrl('/')
      }, {
        '@type': 'ListItem',
        position: 2,
        name: 'Venture Capital',
        item: canonicalUrl('/venture-capital')
      }]
    }]
  });
  return <>
      <main id="main" data-id="element-1732" data-mp="1jr9yn4yysjo1">
        <PageHero headingId="vc-page-heading" breadcrumb="Venture Capital" kicker="DiligenceGPT for Venture Capital" title={<>
              AI That Runs <em data-id="element-1734" data-mp="1xk9bez7d3zrw">Your Dealflow.</em>
            </>} description={<>
              From the first company in your inbox to investment committee and portfolio monitoring.
              <span className="mt-3 block font-semibold text-night-text" data-id="element-1735" data-mp="15stjqqqy7mut">{BRAND.tagline}</span>
            </>} actions={<>
              <CtaButton href={GET_STARTED_URL} variant="violet" size="xl" event="primary_cta_click" eventParams={{
          placement: 'vc_hero',
          cta: 'run_first_deal'
        }} className="w-full sm:w-auto" data-id="element-1736" data-mp="19x198q58d6u3">
                Run Your First Deal
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1737" data-mp="30dmx2w7v9l7k" />
              </CtaButton>
              <CtaButton href={BOOK_DEMO_URL} variant="outlineDark" size="xl" event="enterprise_demo_click" eventParams={{
          placement: 'vc_hero',
          cta: 'see_on_thesis'
        }} className="w-full sm:w-auto" data-id="element-1738" data-mp="2vdii5amlwyjg">
                See DGPT on Your Thesis
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1739" data-mp="2hnx6klb4zllg" />
              </CtaButton>
            </>} aside={<VideoFacade data-id="element-1740" data-mp="27og40cr6lntp" />} data-id="element-1733" data-mp="1orxmjhrwcke1" />

        <section aria-labelledby="vc-workflow-heading" className="border-b border-hairline-soft bg-surface" data-id="element-1741" data-mp="27phdwdwgo49u">
          <div className="mx-auto w-full max-w-[1320px] px-4 py-20 sm:px-6 lg:px-8 lg:py-24" data-id="element-1742" data-mp="23o5vasc08jb5">
            <SectionHeading eyebrow="The venture workflow" headingId="vc-workflow-heading" title="Seven stages. Specialized agents underneath each one." description="Your team owns every decision point. Agents handle the intake, research, follow-up and preparation in between." data-id="element-1743" data-mp="5vruxmruw660" />
            <Reveal className="mt-12" data-id="element-1744" data-mp="dvel3fjk2fhr">
              <ProcessFlow steps={VC_STAGES} ariaLabel="Venture capital workflow with agents" data-id="element-1745" data-mp="3w2c6u2drfxgg" />
            </Reveal>
          </div>
        </section>

        <VcCapabilities data-id="element-1746" data-mp="41fosx65xpxq" />

        <section className="dgpt-dark bg-night" aria-labelledby="memory-heading" data-id="element-1747" data-mp="3mdu8drsye2fz">
          <InstitutionalMemory headingLevel="h2" data-id="element-1748" data-mp="3r4anrhgc3y4z" />
        </section>

        <SampleOutputSection data-id="element-1749" data-mp="3r5lr5g5vb3vk" />
        <EvidenceSection data-id="element-1750" data-mp="3cfekryt4xbb8" />
        <ToolsGallery data-id="element-1751" data-mp="3cg64ab9gjil5" />

        <section aria-labelledby="vc-faq-heading" className="border-b border-hairline-soft bg-surface" data-id="element-1752" data-mp="3cgppq1ybgp3x">
          <div className="mx-auto grid w-full max-w-[1320px] gap-10 px-4 py-20 sm:px-6 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] lg:px-8 lg:py-24" data-id="element-1753" data-mp="3hf0uvzdo2bft">
            <SectionHeading eyebrow="FAQ" headingId="vc-faq-heading" title="Questions from venture teams." data-id="element-1754" data-mp="38djgqmjtkjtr" />
            <Reveal data-id="element-1755" data-mp="2orhqhovksffi">
              <FaqList items={VC_FAQ} data-id="element-1756" data-mp="2ossqddlwh31c" />
            </Reveal>
          </div>
        </section>

        <ProcessCta placement="vc" title="Run Your First Deal." body="Bring a pitch deck and your thesis. See how DiligenceGPT structures the work around your decision." primaryLabel="Run Your First Deal" data-id="element-1757" data-mp="2jty9wibenqke" />
      </main>
      <MobileStickyCta placement="vc_mobile_sticky" data-id="element-1758" data-mp="37qlweortlt2y" />
    </>;
}